import { NavItem, NumberFormat, Select, SelectProps } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'

import { TaxRate } from '@modules-deprecated/app/taxrates/types'

import { getCurrentLocale } from '../../../../utils'
import { useFetchPurchaseTaxRates } from '../../hooks/useFetchPurchaseTaxRates'
import { getVatRateSelectItems } from './utils/getVatRateSelectItems'

interface ReviewEntryVatRateSelectProps
  extends Omit<SelectProps<TaxRate>, 'dropdownFetching' | 'getDisplayValue' | 'items' | 'onSelect'> {
  onSelect?: (id?: string, value?: TaxRate) => void
}

// Purpose-built for the review entries line-item VAT select. The open menu lists each rate's API
// name (percentage, kind, and category, e.g. "0% Goods EU"). The closed field stays the percentage.
// Kept local so the shared `TaxRateSelect` is unchanged. Fetching reuses `useFetchPurchaseTaxRates`.
export const ReviewEntryVatRateSelect = ({
  onSelect,
  selectedId,
  ...selectProps
}: ReviewEntryVatRateSelectProps): ReactElement => {
  const locale = getCurrentLocale()
  const { isLoading, purchaseTaxRates } = useFetchPurchaseTaxRates({ selectedTaxRateId: selectedId })

  const items = useMemo(() => getVatRateSelectItems({ taxRates: purchaseTaxRates, locale }), [purchaseTaxRates, locale])

  const getDisplayValue = useCallback(
    (item: NavItem<TaxRate>) => NumberFormat.getPercentNumber(item.value.rate, locale),
    [locale],
  )

  return (
    <Select
      {...selectProps}
      dropdownFetching={isLoading}
      getDisplayValue={getDisplayValue}
      items={items}
      onSelect={onSelect}
      selectedId={selectedId}
    />
  )
}
